import { formatISO9075 } from 'date-fns';
import { getDocumentAsync } from 'expo-document-picker';
import { ImageManipulator, SaveFormat } from 'expo-image-manipulator';
import {
  type ImagePickerAsset,
  type ImagePickerResult,
  launchCameraAsync,
  launchImageLibraryAsync,
} from 'expo-image-picker';
import {
  UIImagePickerControllerQualityType,
  UIImagePickerPresentationStyle,
} from 'expo-image-picker/build/ImagePicker.types';
import { getImageSize } from 'lib/getImageSize';
import { Platform } from 'react-native';
import {
  FILE_TYPES,
  MAX_FILE_SIZE,
  FileBeingAdded,
  FileType,
  FileTypeExtensions,
} from 'service/file/types';
import { hapticSelection } from 'lib/withHapticFeedback';
import * as MediaLibrary from 'expo-media-library';
import { File as ExpoFile } from 'expo-file-system';
import React from 'react';

export const getDateFormattedForFilename = (): string =>
  formatISO9075(new Date(), { format: 'basic' });

export async function returnFileAsBase64({
  file,
  resizeConfig,
}: {
  file: FileBeingAdded;
  resizeConfig?: {
    width: number;
    height: number;
    maxWidth: number;
    maxHeight: number;
    compression: number;
  };
}): Promise<string> {
  const uri = file.uri || (await readAsDataURLAsync(file.file!));
  if (resizeConfig) {
    return (
      await resizeImage({
        uri,
        width: resizeConfig.width,
        height: resizeConfig.height,
        maxWidth: resizeConfig.maxWidth,
        maxHeight: resizeConfig.maxHeight,
        compression: resizeConfig.compression,
      })
    ).base64;
  }
  return Platform.OS === 'web'
    ? removeDataBase64Prefix(uri)
    : await new ExpoFile(uri).base64();
}

export const readAsDataURLAsync = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const getFileTypeFromUntrustedExtension = (
  extension: FileTypeExtensions | string,
): FileType | undefined => FILE_TYPES[extension as FileTypeExtensions];

export const FALLBACK_CONTENT_TYPE = 'application/octet-stream';

export const getContentTypeFromFileName = (fileName: string): string => {
  const extension = fileName.split('.').pop();
  return extension
    ? (getFileTypeFromUntrustedExtension(extension)?.mime ??
        FALLBACK_CONTENT_TYPE)
    : FALLBACK_CONTENT_TYPE;
};

export const getFileContentType = (file: FileBeingAdded): string =>
  file.file?.type || file.mimeType || getContentTypeFromFileName(file.name);

export const getFileNameExtension = (fileName: string): string | undefined =>
  fileName.split('.').pop()?.toLowerCase();

export const isSupportedFileType = (
  fileName: string | undefined,
  contentType: string | undefined,
  allowedExtensions: FileTypeExtensions[] = Object.values(FileTypeExtensions),
): boolean => {
  const normalisedContentType = contentType?.toLowerCase();
  const extension = fileName ? getFileNameExtension(fileName) : undefined;

  return allowedExtensions.some((allowedExtension) => {
    const fileType = FILE_TYPES[allowedExtension];
    return (
      (normalisedContentType &&
        fileType.mime.toLowerCase() === normalisedContentType) ||
      extension === allowedExtension
    );
  });
};

export const BrowserFileValidationError = {
  notBrowserFile: 'not-browser-file',
  unsupportedFileType: 'unsupported-file-type',
  fileTooLarge: 'file-too-large',
} as const;

export type BrowserFileValidationErrorCode =
  (typeof BrowserFileValidationError)[keyof typeof BrowserFileValidationError];

export const validateFile = (
  file: FileBeingAdded,
  {
    maxFileSizeBytes,
    allowedFileTypes,
  }: {
    maxFileSizeBytes?: number;
    allowedFileTypes?: FileTypeExtensions[];
  } = {},
): { error: string; errorCode: BrowserFileValidationErrorCode } | undefined => {
  const hasReadableFile =
    file.file !== undefined
      ? typeof File !== 'undefined' && file.file instanceof File
      : !!file.uri;
  if (!hasReadableFile) {
    return {
      errorCode: BrowserFileValidationError.notBrowserFile,
      error: 'File type is not supported.',
    };
  }

  if (
    !isSupportedFileType(file.name, getFileContentType(file), allowedFileTypes)
  ) {
    return {
      errorCode: BrowserFileValidationError.unsupportedFileType,
      error: 'File type is not supported.',
    };
  }

  const fileSize = file.size || file.file?.size || 1;
  if (maxFileSizeBytes !== undefined && fileSize > maxFileSizeBytes) {
    return {
      errorCode: BrowserFileValidationError.fileTooLarge,
      error: `File must be ${formatFileSize(maxFileSizeBytes)} or smaller.`,
    };
  }

  return undefined;
};

export const getAllowedFilePickerTypes = (
  fileTypes: Record<FileTypeExtensions, FileType> = FILE_TYPES,
): string[] =>
  Array.from(
    new Set(
      Object.entries(fileTypes).flatMap(([extension, fileType]) => [
        `.${extension}`,
        fileType.mime,
        fileType.uti,
      ]),
    ),
  );

export const formatFileSize = (size: number): string => {
  if (size < 1024) {
    return `${size} B`;
  }
  if (size < 1024 ** 2) {
    return `${Math.round(size / 1024)} KB`;
  }
  return `${Math.round(size / 1024 ** 2)} MB`;
};

export const isImageContentType = (contentType: string): boolean =>
  contentType.startsWith('image/');

export const isVideoFileType = (
  fileName: string,
  contentType: string,
): boolean => {
  const extension = getFileNameExtension(fileName);
  return (
    contentType.startsWith('video/') ||
    extension === FileTypeExtensions.mp4 ||
    extension === FileTypeExtensions.mov
  );
};

export const isPDFFileType = (
  fileName: string,
  contentType: string,
): boolean => {
  const extension = getFileNameExtension(fileName);
  return (
    contentType === FILE_TYPES.pdf.mime || extension === FileTypeExtensions.pdf
  );
};

export const getDroppedFiles = (
  dataTransfer:
    | {
        files?: FileList | File[];
        items?: DataTransferItemList;
      }
    | undefined,
): File[] => {
  const items = dataTransfer?.items ? Array.from(dataTransfer.items) : [];
  const filesFromItems = items
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((file): file is File => !!file);

  if (filesFromItems.length) {
    return filesFromItems;
  }

  return dataTransfer?.files ? Array.from(dataTransfer.files) : [];
};

export const getWebFileObjectUrl = (file: FileBeingAdded): string | null =>
  file.file ? URL.createObjectURL(file.file) : null;

export const revokeObjectUrls = (objectUrls: readonly string[]) => {
  objectUrls.forEach((objectUrl) => {
    if (objectUrl) {
      URL.revokeObjectURL(objectUrl);
    }
  });
};

export const getFileBeingAddedFromDroppedFile = (
  file: File,
): FileBeingAdded => ({
  file,
  uri: undefined,
  name: file.name,
  size: file.size,
  width: 0,
  height: 0,
  typeGroup: null,
});

export const useFileDropTarget = ({
  enabled = Platform.OS === 'web',
  onDropFiles,
  targetId,
}: {
  enabled?: boolean;
  onDropFiles: (files: File[]) => void;
  targetId: string;
}): {
  isDraggingFile: boolean;
  isDraggingOverTarget: boolean;
  resetDragState: () => void;
} => {
  const [isDraggingFile, setIsDraggingFile] = React.useState(false);
  const [isDraggingOverTarget, setIsDraggingOverTarget] = React.useState(false);

  const resetDragState = React.useCallback(() => {
    setIsDraggingFile(false);
    setIsDraggingOverTarget(false);
  }, []);

  React.useEffect(() => {
    if (!enabled) {
      return;
    }

    const getDropTarget = () => document.getElementById(targetId);
    const isInDropTarget = (event: DragEvent) => {
      const dropTarget = getDropTarget();
      return !!(
        dropTarget &&
        event.target instanceof Node &&
        dropTarget.contains(event.target)
      );
    };

    const handleDocumentDragOver = (event: DragEvent) => {
      if (!event.dataTransfer) {
        return;
      }

      const isInsideTarget = isInDropTarget(event);
      event.preventDefault();
      event.dataTransfer.dropEffect = isInsideTarget ? 'copy' : 'none';
      setIsDraggingFile(true);
      setIsDraggingOverTarget(isInsideTarget);
    };

    const handleDocumentDrop = (event: DragEvent) => {
      event.preventDefault();
      const isInsideTarget = isInDropTarget(event);
      const droppedFiles = getDroppedFiles(event.dataTransfer || undefined);

      if (!isInsideTarget) {
        resetDragState();
        return;
      }

      event.stopPropagation();
      resetDragState();
      if (droppedFiles.length) {
        onDropFiles(droppedFiles);
      }
    };

    const handleDocumentDragLeave = (event: DragEvent) => {
      // For if the user drags outside the app browser window
      if (!event.relatedTarget) {
        resetDragState();
      }
    };

    const handleVisibilityChange = () => {
      // If user switches tabs/ no longer has app browser
      // window as primary focus
      if (document.visibilityState !== 'visible') {
        resetDragState();
      }
    };

    document.addEventListener('dragover', handleDocumentDragOver, true);
    document.addEventListener('dragleave', handleDocumentDragLeave, true);
    document.addEventListener('drop', handleDocumentDrop, true);
    document.addEventListener('dragend', resetDragState, true);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('blur', resetDragState);
    window.addEventListener('pagehide', resetDragState);

    return () => {
      document.removeEventListener('dragover', handleDocumentDragOver, true);
      document.removeEventListener('dragleave', handleDocumentDragLeave, true);
      document.removeEventListener('drop', handleDocumentDrop, true);
      document.removeEventListener('dragend', resetDragState, true);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('blur', resetDragState);
      window.removeEventListener('pagehide', resetDragState);
    };
  }, [enabled, onDropFiles, resetDragState, targetId]);

  return {
    isDraggingFile,
    isDraggingOverTarget,
    resetDragState,
  };
};

const mbToBytes = (mb: number) => 2 ** 20 * mb;

export const validateFileSize = (
  file: FileBeingAdded,
  maxFileSizeMb?: number,
): boolean => {
  if (
    !file.size ||
    file.size > (maxFileSizeMb ? mbToBytes(maxFileSizeMb) : MAX_FILE_SIZE)
  ) {
    return false;
  }
  return true;
};

export const removeDataBase64Prefix = (value: string): string =>
  value.replace(/^((data:)[a-z]+\/[a-z-.+]+(;base64,))/, '');

export const getDocument: (options?: {
  type?: string | string[];
  multiple?: boolean;
}) => Promise<FileBeingAdded[]> = async (options) => {
  if (Platform.OS === 'web') {
    const result = await getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: !!options?.multiple,
      type: options?.type ?? '*/*',
    });
    const validatedFiles: FileBeingAdded[] = [];
    if (result.assets) {
      const outputLength = result.assets.length;
      for (let i = 0; i < outputLength; i += 1) {
        const file = result.assets[i];
        if (file?.file && file.size !== undefined) {
          validatedFiles.push({
            file: file.file,
            uri: undefined,
            name: file.name,
            size: file.size,
            width: 0,
            height: 0,
            typeGroup: null,
          });
        }
      }
    }
    return validatedFiles;
  }
  const result = await getDocumentAsync({
    copyToCacheDirectory: true,
    type: options?.type ?? '*/*',
    multiple: !!options?.multiple,
  });
  if (!result.assets) {
    return [];
  }

  return result.assets.map((asset) => ({
    file: undefined,
    uri: asset.uri,
    size: asset.size || 1,
    name: asset.name,
    mimeType: asset.mimeType,
    width: 0,
    height: 0,
    typeGroup: null,
  }));
};

export const getImage = async (options?: {
  multiple?: boolean;
  allowVideo?: boolean;
}): Promise<FileBeingAdded[]> => {
  const allowVideo = !!options?.allowVideo;
  // Web
  if (Platform.OS === 'web') {
    const result = await getDocumentAsync({
      copyToCacheDirectory: true,
      multiple: !!options?.multiple,
      type: allowVideo ? ['image/*', 'video/*'] : 'image/*',
    });
    const validatedFiles: FileBeingAdded[] = [];
    if (result.assets) {
      const outputLength = result.assets.length;
      for (let i = 0; i < outputLength; i += 1) {
        const file = result.assets[i];
        if (file?.file && file.size !== undefined) {
          validatedFiles.push({
            file: file.file,
            uri: undefined,
            name: file.name,
            size: file.size,
            width: 0,
            height: 0,
            typeGroup: null,
          });
        }
      }
    }
    return validatedFiles;
  }
  // Native
  const result: ImagePickerResult = await launchImageLibraryAsync({
    // quality: 0.4,
    allowsEditing: false,
    allowsMultipleSelection: !!options?.multiple,
    mediaTypes: allowVideo ? ['images', 'videos'] : ['images'],
    videoQuality: UIImagePickerControllerQualityType.Medium,
    shouldDownloadFromNetwork: true,
  });
  if (result.canceled) {
    return [];
  }
  return result.assets.map((asset, index) =>
    getFileBeingAddedFromImagePickerAsset(
      asset,
      index,
      result.assets.length,
      asset.type === 'image'
        ? 'image'
        : asset.type === 'video'
          ? 'video'
          : null,
    ),
  );
};

export enum GetPhotoErrors {
  cannotSaveBackup = 'cannotSaveBackup',
}

export const getPhoto = async ({
  saveToMediaLibrary,
}: {
  saveToMediaLibrary: boolean;
}): Promise<FileBeingAdded[]> => {
  // No camera option available for web
  if (Platform.OS === 'web') {
    return [];
  }
  hapticSelection();
  const result = await launchCameraAsync({
    // quality: 0.4,
    allowsEditing: false,
    presentationStyle: UIImagePickerPresentationStyle.OVER_FULL_SCREEN,
  });
  if (result.canceled) {
    return [];
  }
  if (saveToMediaLibrary) {
    try {
      await Promise.all(
        result.assets.map((asset) =>
          MediaLibrary.saveToLibraryAsync(asset.uri),
        ),
      );
    } catch (_) {
      throw new Error(GetPhotoErrors.cannotSaveBackup);
    }
  }
  // Images get resized so we don't need the base64 version of them
  return result.assets.map((asset, index) =>
    getFileBeingAddedFromImagePickerAsset(
      asset,
      index,
      result.assets.length,
      'image',
    ),
  );
};

const getFileBeingAddedFromImagePickerAsset = (
  asset: ImagePickerAsset,
  index: number,
  assetCount: number,
  typeGroup: FileBeingAdded['typeGroup'],
): FileBeingAdded => {
  const fallbackExtension =
    getFileNameExtension(asset.uri) || (typeGroup === 'video' ? 'mov' : 'jpeg');

  return {
    file: undefined,
    uri: asset.uri,
    name:
      asset.fileName ||
      `${getDateFormattedForFilename()}${
        assetCount > 1 ? ` - ${index + 1}` : ''
      }.${fallbackExtension}`,
    width: asset.width,
    height: asset.height,
    size: asset.fileSize || 0,
    mimeType: asset.mimeType,
    typeGroup,
  };
};

export const resizeImage = async ({
  uri,
  width: inputWidth,
  height: inputHeight,
  maxWidth,
  maxHeight,
  compression,
}: {
  uri: string;
  width: number;
  height: number;
  maxWidth: number;
  maxHeight: number;
  compression: number;
}): Promise<{ base64: string; width: number; height: number }> => {
  const { height, width } =
    inputWidth && inputHeight
      ? { height: inputHeight, width: inputWidth }
      : await getImageSize(uri);

  const resize =
    width <= maxWidth && height <= maxHeight
      ? { height, width }
      : width > height
        ? { width: maxWidth }
        : { height: maxHeight };

  const imageRef = await ImageManipulator.manipulate(uri)
    .resize(resize)
    .renderAsync();

  const imageResult = await imageRef.saveAsync(
    compression
      ? {
          compress: compression / 100,
          format: SaveFormat.JPEG,
          base64: true,
        }
      : { compress: 1, format: SaveFormat.JPEG, base64: true },
  );

  return {
    base64: imageResult.base64!,
    width: imageResult.width,
    height: imageResult.height,
  };
};
